import React, { useState } from "react";
import { api, API, formatError } from "@/lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, Copy, KeyRound } from "@/lib/icons";
import { toast } from "sonner";

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Preview ready: ${data.valid_count} valid, ${data.error_count} errors`);
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post(`/admin/import/commit/${preview.batch_id}`);
      setResult(data); toast.success(`${data.inserted} members imported`);
    } catch (e) { toast.error(formatError(e)); } finally { setCommitting(false); }
  };

  const downloadFile = async (path, name) => {
    try {
      const response = await fetch(`${API}${path}`, { credentials: "include" });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const b = await response.blob();
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || "Download failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Bulk onboarding</div>
          <h1 className="mt-2 font-display text-5xl text-white">Import members</h1>
          <p className="admin-page-description">Upload Excel or CSV, validate every row, resolve errors and create accounts safely.</p>
        </div>
        <button data-testid="download-template" onClick={() => downloadFile("/admin/import/template.xlsx", "pronet_import_template.xlsx")}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white">
          <Download size={14} /> Sample template
        </button>
      </div>

      <div className="admin-surface p-5 sm:p-8">
        <label data-testid="upload-dropzone"
          className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-fuchsia-500/30 bg-white/[0.03] p-12 cursor-pointer hover:border-fuchsia-500/60 hover:bg-white/[0.05] transition-all">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/30 to-magenta-500/30 border border-white/10">
            <FileSpreadsheet size={28} className="text-fuchsia-200" />
          </span>
          <div className="font-display text-2xl text-white">
            {file ? file.name : "Choose a .xlsx or .csv file"}
          </div>
          <div className="text-xs text-white/45">Required columns: member_id, full_name, mobile</div>
          <input type="file" className="hidden" accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-input" />
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={upload} disabled={!file || busy} data-testid="upload-preview-btn"
            className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
            <Upload size={14} /> {busy ? "Analyzing…" : "Preview & validate"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="admin-surface overflow-hidden fade-up">
          <div className="p-6 border-b border-white/5 flex flex-wrap items-center gap-6 justify-between">
            <div className="flex gap-8">
              <Stat label="Total rows" value={preview.total_rows} testid="preview-total" />
              <Stat label="Valid" value={preview.valid_count} tone="ok" testid="preview-valid" />
              <Stat label="Errors" value={preview.error_count} tone={preview.error_count ? "warn" : "muted"} testid="preview-errors" />
            </div>
            <div className="flex gap-2">
              {preview.error_count > 0 && (
                <button onClick={() => downloadFile(`/admin/import/${preview.batch_id}/errors.csv`, `import_errors_${preview.batch_id}.csv`)}
                  data-testid="download-errors"
                  className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white">
                  <Download size={14} /> Error report
                </button>
              )}
              <button onClick={commit} disabled={committing || preview.valid_count === 0 || !!result} data-testid="commit-import"
                className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                <CheckCircle2 size={14} /> {committing ? "Importing…" : `Import ${preview.valid_count} members`}
              </button>
            </div>
          </div>

          {result && (
            <>
              <div className="mx-6 mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200" data-testid="commit-success">
                <CheckCircle2 size={14} className="inline mr-1" /> Imported {result.inserted} members successfully.
                {result.credentials?.length > 0 && <> Login credentials are ready to download.</>}
              </div>
              {result.credentials?.length > 0 && (
                <CredentialsPanel credentials={result.credentials} />
              )}
            </>
          )}

          <div className="overflow-x-auto max-h-[540px]">
            <table className="w-full text-xs">
              <thead className="bg-white/5 text-white/60 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Row</th>
                  <th className="text-left px-3 py-2">Member ID</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Mobile</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">City</th>
                  <th className="text-left px-3 py-2">Errors</th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr key={r.row} className={`border-t border-white/5 ${r.errors?.length ? "bg-red-500/5" : ""}`}>
                    <td className="px-3 py-2 font-mono text-white/40">{r.row}</td>
                    <td className="px-3 py-2 font-mono text-white/80">{r.member_id || "-"}</td>
                    <td className="px-3 py-2 text-white/80">{r.full_name || "-"}</td>
                    <td className="px-3 py-2 tabular-nums text-white/70">{r.mobile || "-"}</td>
                    <td className="px-3 py-2 text-white/70">{r.category || "-"}</td>
                    <td className="px-3 py-2 text-white/70">{r.city || "-"}</td>
                    <td className="px-3 py-2">
                      {r.errors?.length ? (
                        <span className="inline-flex items-center gap-1 text-red-300"><AlertTriangle size={12} /> {r.errors.join("; ")}</span>
                      ) : (<span className="text-emerald-300">OK</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "muted", testid }) {
  const color = { ok: "text-emerald-300", warn: "text-amber-300", muted: "text-white" }[tone];
  return (
    <div data-testid={testid}>
      <div className={`font-display text-3xl tabular-nums ${color}`}>{value}</div>
      <div className="overline text-white/50 text-[10px]">{label}</div>
    </div>
  );
}

function CredentialsPanel({ credentials }) {
  const escapeCsv = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = () => {
    const header = ["member_id", "full_name", "username", "email", "mobile"];
    const rows = credentials.map((c) => header.map((k) => escapeCsv(c[k])).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `pronet_import_usernames_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const copyAll = async () => {
    const text = credentials
      .map((c) => `${c.username}\t${c.full_name}\t${c.email || c.mobile}`)
      .join("\n");
    await navigator.clipboard.writeText(text);
    toast.success(`${credentials.length} usernames copied`);
  };
  return (
    <div className="mx-6 my-6 rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/[0.05] overflow-hidden" data-testid="credentials-panel">
      <div className="p-4 md:p-5 flex flex-wrap items-center justify-between gap-3 border-b border-white/5">
        <div>
          <div className="overline text-fuchsia-300 text-[10px] flex items-center gap-2"><KeyRound size={11} /> Generated usernames</div>
          <div className="mt-1 text-sm text-white/70">
            {credentials.length} account{credentials.length === 1 ? "" : "s"} created. Click <b>Send login details</b> on each member to email them a link to set their own password.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={copyAll} data-testid="creds-copy-all"
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white">
            <Copy size={12} /> Copy all
          </button>
          <button onClick={downloadCsv} data-testid="creds-download-csv"
            className="btn-glow inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white">
            <Download size={12} /> Download .csv
          </button>
        </div>
      </div>
      <div className="max-h-[380px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5 text-white/60 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Member ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Username</th>
              <th className="text-left px-3 py-2">Contact</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="px-3 py-2 font-mono text-white/70">{c.member_id}</td>
                <td className="px-3 py-2 text-white/85">{c.full_name}</td>
                <td className="px-3 py-2 font-mono text-fuchsia-200">{c.username}</td>
                <td className="px-3 py-2 text-white/60">{c.email || c.mobile || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
